import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { Loader2, Check, ChevronRight } from "lucide-react";

/**
 * /app/join?token=gc_xxx — handles the actual group-join after auth.
 * If not authenticated yet, we redirect to /login?next=/app/join?token=...
 */
export default function Join() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nextPath = params.get("next") || "/app";
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("joining");
  const [group, setGroup] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!token) {
      navigate("/app");
      return;
    }
    if (!user || !user.user_id) {
      const target = `/app/join?token=${token}` + (nextPath !== "/app" ? `&next=${encodeURIComponent(nextPath)}` : "");
      navigate(`/login?next=${encodeURIComponent(target)}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/groups/join-by-token", { token });
        setGroup(data);
        setState("done");
      } catch (e) {
        setErr(formatError(e));
        setState("error");
      }
    })();
  }, [token, user, loading, navigate, nextPath]);

  return (
    <AppShell>
      <div className="grid place-items-center py-12">
        <div className="bd-surface p-8 w-full max-w-md text-center" data-testid="join-card">
          {state === "joining" && (
            <>
              <Loader2 className="animate-spin mx-auto mb-3" />
              <h2 className="text-xl font-bold">Joining your group…</h2>
            </>
          )}
          {state === "done" && group && (
            <>
              <span
                className="w-14 h-14 grid place-items-center mx-auto mb-3"
                style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: 9999 }}
              >
                <Check size={24} />
              </span>
              <h2 className="text-2xl font-extrabold mb-2" style={{ letterSpacing: "-0.03em" }}>You&apos;re in.</h2>
              <p className="mb-4" style={{ color: "var(--text-dim)" }}>
                Welcome to <strong style={{ color: "var(--text)" }}>{group.name}</strong>.
              </p>
              <button onClick={() => navigate("/app")} className="bd-btn" data-testid="join-continue">
                See the calendar <ChevronRight size={14} />
              </button>
            </>
          )}
          {state === "error" && (
            <>
              <h2 className="text-xl font-bold mb-2">Couldn&apos;t join</h2>
              <p style={{ color: "var(--text-dim)" }}>{err}</p>
              <button onClick={() => navigate("/app")} className="bd-btn mt-4" data-testid="join-go-home">
                Go to my calendar
              </button>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
